import { useCallback, useEffect, useState } from 'react';
import { api, ErroApi } from '../lib/api';
import { enviarArquivo } from '../lib/quadro';
import { Alerta, Botao, Cartao } from './Interface';
import { useConfirmacao } from './Confirmacao';

type Banner = {
  id: number;
  titulo: string | null;
  caminho: string;
  link: string | null;
  ativo: boolean;
  ordem: number;
};

/**
 * Faixa do alto do "Meu dia". A imagem se adapta sozinha: no computador fica
 * larga e baixa, no celular ocupa a largura toda sem cortar o essencial.
 */
export default function BannerTopo({ podeEditar }: { podeEditar: boolean }) {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [gerenciando, setGerenciando] = useState(false);
  const [todos, setTodos] = useState<Banner[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const { confirmar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const d = await api<{ banners: Banner[] }>('/banners');
      setBanners(d.banners);
    } catch {
      setBanners([]);
    }
  }, []);

  const carregarTodos = useCallback(async () => {
    try {
      const d = await api<{ banners: Banner[] }>('/banners?todos=1');
      setTodos(d.banners);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar os banners.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    if (gerenciando) void carregarTodos();
  }, [gerenciando, carregarTodos]);

  async function enviar(arquivo: File) {
    setErro(null);
    setEnviando(true);
    try {
      const subido = await enviarArquivo(arquivo);
      await api('/banners', { method: 'POST', body: JSON.stringify({ caminho: subido.caminho }) });
      await Promise.all([carregar(), carregarTodos()]);
    } catch (e) {
      // ARMADILHA 12: falha de envio aparece, nao some
      setErro(e instanceof Error ? e.message : 'Não consegui enviar a imagem.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      {banners.length > 0 && (
        <div className="mb-4 space-y-3">
          {banners.map((b) => {
            const imagem = (
              <img
                src={`/api/uploads/${b.caminho}`}
                alt={b.titulo ?? 'Aviso da igreja'}
                loading="lazy"
                className="h-40 w-full rounded-2xl object-cover shadow-cartao sm:h-48 md:h-56"
              />
            );
            return b.link ? (
              <a key={b.id} href={b.link} target="_blank" rel="noopener noreferrer nofollow" className="block">
                {imagem}
              </a>
            ) : (
              <div key={b.id}>{imagem}</div>
            );
          })}
        </div>
      )}

      {podeEditar && (
        <div className="mb-4 flex items-center gap-3">
          <button
            onClick={() => setGerenciando((g) => !g)}
            className="text-sm font-medium text-tinta-400 hover:text-tinta-900"
          >
            {banners.length === 0 ? '+ colocar um banner no topo' : 'trocar banner'}
          </button>
        </div>
      )}

      {gerenciando && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-tinta-900/30 p-4"
          onClick={() => setGerenciando(false)}
        >
          <div className="my-auto w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <Cartao>
              <h3 className="text-lg font-semibold text-tinta-900">Banner do topo</h3>
              <p className="mt-1 text-sm text-tinta-400">
                Aparece acima do versiculo, para todo mundo que entra. Use uma imagem larga
                (por exemplo 1600 x 500) — no celular ela se ajusta sozinha.
              </p>

              {erro && (
                <div className="mt-3">
                  <Alerta>{erro}</Alerta>
                </div>
              )}

              <label className="mt-4 flex cursor-pointer items-center justify-center rounded-xl border border-dashed border-tinta-200 px-3 py-6 text-sm font-medium text-tinta-900 hover:border-tinta-900">
                {enviando ? 'Enviando...' : 'Escolher imagem (até 25 MB)'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const arquivo = e.target.files?.[0];
                    e.target.value = '';
                    if (arquivo) void enviar(arquivo);
                  }}
                />
              </label>

              <ul className="mt-4 space-y-2">
                {todos.map((b) => (
                  <li key={b.id} className="flex items-center gap-3 rounded-xl bg-tinta-100 p-2">
                    <img
                      src={`/api/uploads/${b.caminho}`}
                      alt=""
                      className="h-12 w-24 shrink-0 rounded-lg object-cover"
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-tinta-700">
                      {b.titulo ?? 'Sem título'}
                    </span>
                    <button
                      onClick={async () => {
                        await api(`/banners/${b.id}`, {
                          method: 'PATCH',
                          body: JSON.stringify({ ativo: !b.ativo }),
                        });
                        await Promise.all([carregar(), carregarTodos()]);
                      }}
                      className={`shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold ${
                        b.ativo ? 'bg-tinta-900 text-white' : 'bg-tinta-200 text-tinta-600'
                      }`}
                    >
                      {b.ativo ? 'no ar' : 'oculto'}
                    </button>
                    <button
                      onClick={async () => {
                        const ok = await confirmar({
                          titulo: 'Remover este banner?',
                          texto: 'Ele sai do topo do sistema. O arquivo continua guardado no servidor.',
                          confirmar: 'Remover',
                          perigo: true,
                        });
                        if (!ok) return;
                        await api(`/banners/${b.id}`, { method: 'DELETE' });
                        await Promise.all([carregar(), carregarTodos()]);
                      }}
                      className="shrink-0 text-xs text-tinta-400 hover:text-red-600"
                    >
                      remover
                    </button>
                  </li>
                ))}
                {todos.length === 0 && (
                  <li className="text-sm text-tinta-400">Nenhum banner ainda.</li>
                )}
              </ul>

              <div className="mt-5 flex justify-end">
                <Botao variante="secundario" onClick={() => setGerenciando(false)}>
                  Fechar
                </Botao>
              </div>
            </Cartao>
          </div>
        </div>
      )}
      {dialogo}
    </>
  );
}
